var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { OperationEvent } from '@augloop/tracelogger-schemas';
import { now } from './now';
import { Log, LogCategory } from './logging';
/**
 * Operation events are used to track the execution of an activity.
 * Default High-Cardinality fields include count, duration, success, resultDescription, resultSignature.
 * Default Low-Cardinality dimension fields include success, resourceId, dimension0, dimension1, dimension2, dimension3 clientAppName, clientAppPlatform.
 * Operations are logged and abide by platform sampling rates.
 * Operations can optionally produce metrics ${operationName}.CountV2 & ${operationName}.DurationV2 which are enabled via options.
  */
var Operation = /** @class */ (function (_super) {
    __extends(Operation, _super);
    /**
     * Creates a new `Operation` object that can be logged
     * @param properties dynamic event details
     * @param options operation options
     */
    function Operation(properties, options) {
        var _this = _super.call(this, properties) || this;
        _this.eventName = 'Operation';
        _this.options = options;
        // Overwrite the protobuf default value of 0
        _this.durationMs = properties === null || properties === void 0 ? void 0 : properties.durationMs;
        if (!properties || properties.count == undefined) {
            _this.count = 1;
        }
        return _this;
    }
    /**
     * Attaches metadata about the client-app that requested this operation
     * @param clientMetadata the client application that requested this operation
     * @returns `this` for fluent API
     */
    Operation.prototype.setClientMetadata = function (clientMetadata) {
        if (clientMetadata) {
            this.clientAppName = clientMetadata.appName;
            this.clientAppPlatform = clientMetadata.appPlatform;
            this.clientAppVersion = clientMetadata.appVersion;
            this.clientFlights = clientMetadata.flights;
            this.clientReleaseAudienceGroup = clientMetadata.releaseAudienceGroup;
            this.clientReleaseChannel = clientMetadata.releaseChannel;
            this.clientReleaseFork = clientMetadata.releaseFork;
            this.clientRuntimeVersion = clientMetadata.runtimeVersion;
            this.clientSessionId = clientMetadata.sessionId;
            this.clientDeviceId = clientMetadata.deviceId;
            this.clientUserAgent = clientMetadata.userAgent;
        }
        return this;
    };
    /**
     * Attaches metadata about the authenticated user that is using the system
     * @param userContext data about the authenticated user
     * @returns `this` for fluent API
     */
    Operation.prototype.setUserContext = function (userContext) {
        if (userContext) {
            // please ensure fields assigned here match those in userContextFieldNames in geneva-trace-logger.ts
            // NOTE: never log EUII, like userContext.username
            this.userId = userContext.puid || userContext.oid;
            this.userType = userContext.userType && userContext.userType.toString();
            this.userTenantId = userContext.tid;
        }
        return this;
    };
    /**
     * Creates a custom data field and assigns value for it
     * @param fieldName description of data being recorded; column in Kusto
     * @param dataFieldValue value being recorded
     */
    Operation.prototype.setDataField = function (fieldName, dataFieldValue) {
        var dataField = this.dataFields ? JSON.parse(this.dataFields) : {};
        dataField[fieldName] = dataFieldValue;
        this.dataFields = JSON.stringify(dataField);
    };
    /**
     * Creates + sets multiple custom data fields
     * @param dataFields descriptions of data/columns in Kusto and their values
     */
    Operation.prototype.setDataFields = function (dataFields) {
        if (!this.dataFields) {
            this.dataFields = JSON.stringify(dataFields);
        }
        else {
            this.dataFields = JSON.stringify(__assign(__assign({}, JSON.parse(this.dataFields)), dataFields));
        }
    };
    /**
     * Marks the start of this operation.
     * @note `durationMs` is not defined at this point.
     * @returns `this` for fluent API
     */
    Operation.prototype.start = function () {
        this.startTime = now();
        return this;
    };
    /**
     * Calculates and sets Operation `durationMs` since `start()` was called
     * @returns `this` for fluent API
     */
    Operation.prototype.stop = function () {
        var stopTime = now();
        this.durationMs = Math.round(stopTime - this.startTime);
        return this;
    };
    Operation.prototype.getMetrics = function (enabledOverrides, pinnedMetrics) {
        var _this = this;
        var _a, _b, _c, _d;
        var metrics = {};
        if (this.operationName) {
            switch (this.operationName) {
                case 'SessionHealthOrphanedEventsWithoutProperSessionKey':
                case 'SessionHealthOrphanedSessions':
                case 'WorkflowActivationState':
                    metrics[this.operationName + '.CountV2'] = {
                        dimensionNames: this.getOrphanedSessionHealthDimensionNames.bind(this),
                        dimensionValues: this.getOrphanedSessionHealthDimensionValues(),
                        value: this.count
                    };
                    break;
                case 'MarkUnhealthySession':
                case 'MarkHealthWarningSession':
                    metrics[this.operationName + '.Reason'] = {
                        dimensionNames: this.getSessionHealthDimensionNames.bind(this),
                        dimensionValues: this.getSessionHealthDimensionValues(),
                        value: 1
                    };
                    break;
                default:
                    if (this.operationName === 'matchmaker_timer') {
                        metrics['Workflow.DurationMs'] = {
                            dimensionNames: this.getWorkflowDimensionNames.bind(this),
                            dimensionValues: this.getWorkflowDimensionValues(),
                            value: this.durationMs || 0
                        };
                        metrics['Workflow.Count'] = {
                            dimensionNames: this.getWorkflowDimensionNames.bind(this),
                            dimensionValues: this.getWorkflowDimensionValues(),
                            value: 1
                        };
                    }
                    {
                        // Don't emit metrics when the duration is missing
                        if (this.durationMs !== undefined) {
                            var metricName = "".concat(this.operationName, ".DurationMsV2");
                            var isPinned = pinnedMetrics[metricName];
                            if (!((_a = this.options) === null || _a === void 0 ? void 0 : _a.metricDuration) && isPinned) {
                                Log.warn(0x1f31b702 /* tag_5m12c */, LogCategory.CoreDefault, function () { return "Please add { metricDuration:true } to operation ".concat(_this.operationName, "."); });
                            }
                            if (((_b = this.options) === null || _b === void 0 ? void 0 : _b.metricDuration) || isPinned || enabledOverrides.indexOf(metricName) >= 0) {
                                metrics[metricName] = {
                                    dimensionNames: this.getDimensionNames.bind(this),
                                    dimensionValues: this.getDimensionValues(),
                                    value: this.durationMs
                                };
                            }
                        }
                    }
                    {
                        var metricName = "".concat(this.operationName, ".CountV2");
                        var isPinned = pinnedMetrics[metricName];
                        if (!((_c = this.options) === null || _c === void 0 ? void 0 : _c.metricCount) && isPinned) {
                            Log.warn(0x1f31b703 /* tag_5m12d */, LogCategory.CoreDefault, function () { return "Please add { metricCount: true } to operation ".concat(_this.operationName, "."); });
                        }
                        if (((_d = this.options) === null || _d === void 0 ? void 0 : _d.metricCount) || isPinned || enabledOverrides.indexOf(metricName) >= 0) {
                            metrics[metricName] = {
                                dimensionNames: this.getDimensionNames.bind(this),
                                dimensionValues: this.getDimensionValues(),
                                value: this.count
                            };
                        }
                    }
            }
        }
        return metrics;
    };
    Operation.prototype.getDimensionNames = function () {
        return [
            'Success', 'ClientAppName', 'ClientAppPlatform', 'ResourceId',
            'Dimension0', 'Dimension1', 'Dimension2', 'Dimension3',
        ];
    };
    Operation.prototype.getDimensionValues = function () {
        return [
            this.success, this.clientAppName, this.clientAppPlatform, this.resourceId,
            this.dimension0, this.dimension1, this.dimension2, this.dimension3,
        ];
    };
    Operation.prototype.getOrphanedSessionHealthDimensionNames = function () {
        return [
            'Success', 'ClientAppName', 'ClientAppPlatform',
            'ClientReleaseAudienceGroup', 'ResourceId',
            'Dimension0', 'Dimension1', 'Dimension2', 'Dimension3',
        ];
    };
    Operation.prototype.getOrphanedSessionHealthDimensionValues = function () {
        return [
            this.success, this.clientAppName, this.clientAppPlatform,
            this.clientReleaseAudienceGroup, this.resourceId,
            this.dimension0, this.dimension1, this.dimension2, this.dimension3,
        ];
    };
    Operation.prototype.getSessionHealthDimensionNames = function () {
        return [
            'Reason',
            'ClientAppName', 'ClientAppPlatform', 'ClientAppVersion',
            'Dimension0', 'Dimension1', 'Dimension2', 'Dimension3',
        ];
    };
    Operation.prototype.getSessionHealthDimensionValues = function () {
        return [
            this.resultSignature,
            this.clientAppName, this.clientAppPlatform, this.clientAppVersion,
            this.dimension0, this.dimension1, this.dimension2, this.dimension3,
        ];
    };
    Operation.prototype.getWorkflowDimensionNames = function () {
        return [
            'ResultSignature', 'WorkflowId', 'ResourceId', 'Success',
        ];
    };
    Operation.prototype.getWorkflowDimensionValues = function () {
        return [
            this.resultSignature, this.resourceId, this.resourceId, this.success,
        ];
    };
    return Operation;
}(OperationEvent));
export { Operation };
//# sourceMappingURL=operation.js.map